import React, { useState, useEffect } from "react";
import { Plus } from "lucide-react";
import { Card } from "components/ui/card";
import { Button } from "components/ui/button";
import PageWrapper from "components/PageWrapper";
import { getActiveDistributorKeys } from "actions/distributorActions";
import DistributorKeyList from "components/settings/DistributorKeyList";
import DistributorKeysSkeleton from "components/skeleton/DistributorKeysSkeleton";
import DistributorConfigModal from "components/settings/DistributorConfigModal";
import QuotationSettings from "components/profile/QuotationSettings";

const SettingsPage = () => {
  const [isDistributorConfigModalOpen, setIsDistributorConfigModalOpen] =
    useState(false);
  const [selectedDistributor, setSelectedDistributor] = useState(null);
  const [editInitialValues, setEditInitialValues] = useState(null);
  const [activeDistributor, setActiveDistributor] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [digikeyData, setDigikeyData] = useState({
    isLoading: false,
    isTokenExpired: false,
    message: "",
  });

  const openConfigure = () => {
    setSelectedDistributor(null);
    setEditInitialValues(null);
    setIsDistributorConfigModalOpen(true);
  };

  const handleEditDistributor = (type) => {
    const match = activeDistributor.find((d) => d.type === type);
    setSelectedDistributor(type);
    setEditInitialValues(match ? { ...match.value, type } : null);
    setIsDistributorConfigModalOpen(true);
  };

  const fetchActiveDistributor = async () => {
    setIsLoading(true);
    const response = await getActiveDistributorKeys();
    if (response.status) {
      const newDistributor = Object.keys(response.data).map((key) => ({
        type: key,
        value: response.data[key],
      }));
      setActiveDistributor(newDistributor);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchActiveDistributor();
  }, []);

  return (
    <PageWrapper>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[28px] leading-[34px] font-bold tracking-[-0.015em] text-s360-ink">
            Settings
          </h1>
          <span className="text-sm text-s360-fg3">
            Configure distributor API connections, and quotation defaults (margins and duty).
          </span>
        </div>

        <section>
          <Card className="p-6 relative overflow-hidden border-t-[3px] border-t-s360-violet">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <h2 className="text-base font-semibold text-s360-fg1">
                  Distributor API keys
                </h2>
                {activeDistributor.length > 0 && (
                  <span className="text-xs text-s360-fg3">
                    {activeDistributor.length - (digikeyData.isTokenExpired ? 1 : 0)} of{" "}
                    {activeDistributor.length} active
                  </span>
                )}
              </div>
              <Button
                size="sm"
                variant="primary"
                className="min-w-[88px] h-9 rounded-md bg-s360-violet text-white shadow-sm hover:bg-s360-violet-600 transition-colors disabled:opacity-60"
                onClick={openConfigure}
              >
                <Plus className="w-4 h-4 mr-2" />
                Configure
              </Button>
            </div>
            <div className="flex flex-col gap-2 min-h-[120px] relative">
              {isLoading ? (
                <DistributorKeysSkeleton />
              ) : (
                activeDistributor.map((distributor) => (
                  <DistributorKeyList
                    key={distributor.type}
                    type={distributor.type}
                    distributorValue={distributor.value}
                    digikeyData={digikeyData}
                    setDigikeyData={setDigikeyData}
                    onEdit={handleEditDistributor}
                  />
                ))
              )}
            </div>
          </Card>
        </section>

        <section>
          <QuotationSettings />
        </section>
      </div>

      <DistributorConfigModal
        isDistributorConfigModalOpen={isDistributorConfigModalOpen}
        setIsDistributorConfigModalOpen={(open) => {
          setIsDistributorConfigModalOpen(open);
          if (!open) {
            setSelectedDistributor(null);
            setEditInitialValues(null);
          }
        }}
        selectedDistributor={selectedDistributor}
        initialValues={editInitialValues}
        onSaveSuccess={fetchActiveDistributor}
      />
    </PageWrapper>
  );
};

export default SettingsPage;
